/**
 * PropertiesPanel — Right sidebar for editing selected node details.
 *
 * Renders context-specific editors based on node type:
 * - Asset: ticker input, name input
 * - Group: name input
 * - Weight-specified: weight sliders
 * - If: condition builder (left signal, operator, right signal/number)
 * - Filter: signal select, window input, direction select, count
 * - Inv-vol: window input
 */

import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useBuilderStore, useSelectedNode } from '../../stores/builderStore';
import { SIGNAL_LABELS, SIGNAL_HAS_WINDOW, SIGNAL_DEFAULT_WINDOW, NODE_LABELS, NODE_COLORS } from '../../types/builder';
import type { SignalType, CompareOperator, Signal, Condition } from '../../types/builder';

const ALL_SIGNALS: SignalType[] = [
  'rsi', 'cumulative-return', 'current-price', 'moving-average-price',
  'exponential-moving-average-price', 'moving-average-return',
  'max-drawdown', 'stdev-return', 'stdev-price',
];

const OPERATORS: CompareOperator[] = ['>', '<', '>=', '<='];

export function PropertiesPanel() {
  const node = useSelectedNode();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!node ? (
          <div className="text-sm text-surface-500 text-center py-8">
            Select a node to edit its properties
          </div>
        ) : (
          <NodeEditor />
        )}
      </div>
    </div>
  );
}

// ─── Node Editor ─────────────────────────────────────────────

function NodeEditor() {
  const node = useSelectedNode();
  if (!node) return null;

  const colors = NODE_COLORS[node.type];

  return (
    <div className="space-y-4">
      {/* Node type badge */}
      <div className="flex items-center gap-2">
        <span className={clsx('text-xs font-bold px-2 py-1 rounded', colors.pill)}>
          {NODE_LABELS[node.type]}
        </span>
        <span className="text-[10px] text-surface-500 font-mono">{node.id.slice(0, 12)}</span>
      </div>

      {/* Type-specific editors */}
      {node.type === 'asset' && <AssetEditor nodeId={node.id} ticker={node.ticker} name={node.name} />}
      {node.type === 'group' && <GroupEditor nodeId={node.id} name={node.name} />}
      {node.type === 'weight-specified' && <WeightEditor nodeId={node.id} weights={node.weights} children={node.children} />}
      {node.type === 'if' && <ConditionEditor nodeId={node.id} condition={node.condition} />}
      {node.type === 'filter' && <FilterEditor nodeId={node.id} signal={node.signal} selector={node.selector} />}
      {node.type === 'weight-inverse-volatility' && <WindowEditor nodeId={node.id} window={node.window} />}
    </div>
  );
}

// ─── Common Field Components ─────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-medium text-surface-400 mb-1">{children}</label>;
}

function TextInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={clsx(
        'w-full px-3 py-1.5 rounded-lg text-sm',
        'bg-surface-900 border border-surface-600 text-surface-100',
        'placeholder:text-surface-600',
        'focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30',
        'transition-colors',
      )}
    />
  );
}

function NumberInput({ value, onChange, min, max, step }: { value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number }) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      min={min}
      max={max}
      step={step ?? 1}
      className={clsx(
        'w-full px-3 py-1.5 rounded-lg text-sm',
        'bg-surface-900 border border-surface-600 text-surface-100',
        'focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30',
        'transition-colors',
      )}
    />
  );
}

function SelectInput<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className={clsx(
        'w-full px-3 py-1.5 rounded-lg text-sm appearance-none',
        'bg-surface-900 border border-surface-600 text-surface-100',
        'focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30',
        'transition-colors cursor-pointer',
      )}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// ─── Type-Specific Editors ───────────────────────────────────

function AssetEditor({ nodeId, ticker, name }: { nodeId: string; ticker: string; name?: string }) {
  const updateAsset = useBuilderStore((s) => s.updateAsset);
  return (
    <>
      <div>
        <Label>Ticker</Label>
        <TextInput
          value={ticker}
          onChange={(v) => updateAsset(nodeId, v.toUpperCase(), name)}
          placeholder="SPY"
        />
      </div>
      <div>
        <Label>Display Name (optional)</Label>
        <TextInput
          value={name || ''}
          onChange={(v) => updateAsset(nodeId, ticker, v || undefined)}
          placeholder="S&P 500 ETF"
        />
      </div>
    </>
  );
}

function GroupEditor({ nodeId, name }: { nodeId: string; name: string }) {
  const updateGroupName = useBuilderStore((s) => s.updateGroupName);
  return (
    <div>
      <Label>Group Name</Label>
      <TextInput value={name} onChange={(v) => updateGroupName(nodeId, v)} placeholder="My Group" />
    </div>
  );
}

function WeightEditor({ nodeId, weights, children }: { nodeId: string; weights: number[]; children: { id: string; type: string; ticker?: string }[] }) {
  const updateWeights = useBuilderStore((s) => s.updateWeights);

  const handleWeight = useCallback(
    (index: number, pct: number) => {
      const newWeights = [...weights];
      newWeights[index] = pct / 100;
      // Normalize to 100%
      const total = newWeights.reduce((a, b) => a + b, 0);
      if (total > 0) {
        const normalized = newWeights.map((w) => w / total);
        updateWeights(nodeId, normalized);
      }
    },
    [nodeId, weights, updateWeights],
  );

  return (
    <div>
      <Label>Weights</Label>
      <div className="space-y-2">
        {children.map((child, i) => {
          const pct = Math.round((weights[i] ?? 0) * 100);
          const childLabel = 'ticker' in child && child.ticker ? child.ticker : `${child.type} #${i + 1}`;
          return (
            <div key={child.id} className="flex items-center gap-2">
              <span className="text-xs text-surface-400 w-16 truncate">{childLabel}</span>
              <input
                type="range"
                min={0}
                max={100}
                value={pct}
                onChange={(e) => handleWeight(i, Number(e.target.value))}
                className="flex-1 accent-primary-500"
              />
              <span className="text-xs text-surface-300 w-10 text-right">{pct}%</span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-surface-500">
        Total: {Math.round(weights.reduce((a, b) => a + b, 0) * 100)}%
      </div>
    </div>
  );
}

function ConditionEditor({ nodeId, condition }: { nodeId: string; condition: Condition }) {
  const updateCondition = useBuilderStore((s) => s.updateCondition);

  const signalOptions = useMemo(
    () => ALL_SIGNALS.map((s) => ({ value: s, label: SIGNAL_LABELS[s] })),
    [],
  );
  const operatorOptions = useMemo(() => OPERATORS.map((o) => ({ value: o, label: o })), []);

  const updateLeft = useCallback(
    (updates: Partial<Signal>) => {
      updateCondition(nodeId, { ...condition, left: { ...condition.left, ...updates } });
    },
    [nodeId, condition, updateCondition],
  );

  const updateRight = useCallback(
    (val: Signal | number) => {
      updateCondition(nodeId, { ...condition, right: val });
    },
    [nodeId, condition, updateCondition],
  );

  const rightIsNumber = typeof condition.right === 'number';

  return (
    <div className="space-y-3">
      {/* Left signal */}
      <div>
        <Label>Left Signal</Label>
        <div className="space-y-1.5">
          <TextInput value={condition.left.ticker} onChange={(v) => updateLeft({ ticker: v.toUpperCase() })} placeholder="SPY" />
          <SelectInput value={condition.left.type} onChange={(v) => updateLeft({ type: v, window: SIGNAL_DEFAULT_WINDOW[v] })} options={signalOptions} />
          {SIGNAL_HAS_WINDOW[condition.left.type] && (
            <NumberInput value={condition.left.window ?? 0} onChange={(v) => updateLeft({ window: v })} min={1} />
          )}
        </div>
      </div>

      {/* Operator */}
      <div>
        <Label>Operator</Label>
        <SelectInput
          value={condition.operator}
          onChange={(v) => updateCondition(nodeId, { ...condition, operator: v })}
          options={operatorOptions}
        />
      </div>

      {/* Right side toggle */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>Right Side</Label>
          <button
            onClick={() => {
              if (rightIsNumber) {
                updateRight({ type: 'rsi', ticker: 'SPY', window: 14 });
              } else {
                updateRight(0);
              }
            }}
            className="text-[10px] text-primary-400 hover:text-primary-300"
          >
            {rightIsNumber ? 'Switch to Signal' : 'Switch to Number'}
          </button>
        </div>
        {rightIsNumber ? (
          <NumberInput value={condition.right as number} onChange={(v) => updateRight(v)} step={0.1} />
        ) : (
          <div className="space-y-1.5">
            <TextInput value={(condition.right as Signal).ticker} onChange={(v) => updateRight({ ...condition.right as Signal, ticker: v.toUpperCase() })} placeholder="QQQ" />
            <SelectInput
              value={(condition.right as Signal).type}
              onChange={(v) => updateRight({ ...condition.right as Signal, type: v, window: SIGNAL_DEFAULT_WINDOW[v] })}
              options={signalOptions}
            />
            {SIGNAL_HAS_WINDOW[(condition.right as Signal).type] && (
              <NumberInput
                value={(condition.right as Signal).window ?? 0}
                onChange={(v) => updateRight({ ...condition.right as Signal, window: v })}
                min={1}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterEditor({ nodeId, signal, selector }: { nodeId: string; signal: { type: SignalType; window?: number }; selector: { direction: 'top' | 'bottom'; count: number } }) {
  const updateFilter = useBuilderStore((s) => s.updateFilter);

  const signalOptions = useMemo(
    () => ALL_SIGNALS.map((s) => ({ value: s, label: SIGNAL_LABELS[s] })),
    [],
  );

  return (
    <div className="space-y-3">
      <div>
        <Label>Filter Signal</Label>
        <SelectInput
          value={signal.type}
          onChange={(v) => updateFilter(nodeId, { type: v, window: SIGNAL_DEFAULT_WINDOW[v] }, selector)}
          options={signalOptions}
        />
      </div>
      {SIGNAL_HAS_WINDOW[signal.type] && (
        <div>
          <Label>Window (days)</Label>
          <NumberInput
            value={signal.window ?? 0}
            onChange={(v) => updateFilter(nodeId, { ...signal, window: v }, selector)}
            min={1}
          />
        </div>
      )}
      <div>
        <Label>Direction</Label>
        <SelectInput
          value={selector.direction}
          onChange={(v) => updateFilter(nodeId, signal, { ...selector, direction: v })}
          options={[{ value: 'top' as const, label: 'Top (highest)' }, { value: 'bottom' as const, label: 'Bottom (lowest)' }]}
        />
      </div>
      <div>
        <Label>Count</Label>
        <NumberInput
          value={selector.count}
          onChange={(v) => updateFilter(nodeId, signal, { ...selector, count: Math.max(1, v) })}
          min={1}
        />
      </div>
    </div>
  );
}

function WindowEditor({ nodeId, window }: { nodeId: string; window: number }) {
  const updateWindow = useBuilderStore((s) => s.updateWindow);
  return (
    <div>
      <Label>Volatility Window (days)</Label>
      <NumberInput value={window} onChange={(v) => updateWindow(nodeId, Math.max(1, v))} min={1} />
    </div>
  );
}
